"use client";

import {
	Printer,
	Plane,
	Bot,
	ArrowUpRight,
	Triangle,
	Circle,
	Square,
	Box,
	HandHelping,
	Palette
} from "lucide-react";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MagneticButton } from "@/components/custom/magnetic-button";

// JSON-LD Service Schema for SEO
const serviceJsonLd = {
	"@context": "https://schema.org",
	"@type": "Service",
	serviceType: "3D Fabrication and Drone Services",
	name: "Elec3D - 3D Fabrication, Drones & Artistic Collectibles",
	description:
		"Precision 3D fabrication, autonomous drone systems, robotics, and artistic sci-fi/anime collectibles. Proudly made in India.",
	provider: {
		"@type": "Organization",
		name: "TheHighrays",
		url: "https://thehighrays.com",
	},
	areaServed: {
		"@type": "Place",
		name: "Worldwide",
	},
	hasOfferCatalog: {
		"@type": "OfferCatalog",
		name: "Elec3D Services",
		itemListElement: [
			{
				"@type": "Offer",
				itemOffered: {
					"@type": "Service",
					name: "3D Fabrication",
					description:
						"Turning digital geometry into physical reality. Precision additive manufacturing.",
				},
			},
			{
				"@type": "Offer",
				itemOffered: {
					"@type": "Service",
					name: "Drone Systems",
					description:
						"Autonomous aerial vehicles designed for surveillance and data acquisition.",
				},
			},
			{
				"@type": "Offer",
				itemOffered: {
					"@type": "Service",
					name: "Art/Sci-Fi/Anime Collectibles",
					description:
						"Curated collection of pre-printed 3D models and artistic collectibles.",
				},
			},
		],
	},
};

const faqJsonLd = {
	"@context": "https://schema.org",
	"@type": "FAQPage",
	mainEntity: [
		{
			"@type": "Question",
			name: "Do you take custom commissions?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Absolutely. We bring your unique ideas to life, from concept art to physical reality.",
			},
		},
		{
			"@type": "Question",
			name: "Do you build custom drone/robotics projects?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Yes. From heavy-lift quadcopters to complex robotics and automation systems.",
			},
		},
		{
			"@type": "Question",
			name: "Where are you based?",
			acceptedAnswer: {
				"@type": "Answer",
				text: "Proudly engineered and manufactured in India, serving a global clientele.",
			},
		},
	],
};

// --- Data ---
const services = [
	{
		id: "01",
		title: "3D Fabrication",
		icon: <Printer className="h-8 w-8" />,
		description: "Turning digital geometry into physical reality. Precision additive manufacturing.",
		shape: "circle",
		color: "bg-red-600"
	},
	{
		id: "02",
		title: "Drone Systems",
		icon: <Plane className="h-8 w-8" />,
		description: "Autonomous aerial vehicles designed for surveillance and data acquisition.",
		shape: "triangle",
		color: "bg-blue-600"
	},
	{
		id: "03",
		title: "ART/SCI-FI/ANIME",
		icon: <Bot className="h-8 w-8" />,
		description: "We offer a curated collection of pre-printed 3D models and artistic collectibles.",
		shape: "square",
		color: "bg-yellow-500"
	},
];

const directors = [
	{
		name: "Capt. Parag Shree Bahera",
		role: "Aerospace",
		bio: "Flight Dynamics & Strategy",
		color: "bg-blue-600"
	},
	{
		name: "Shashi Shekhar Pandey",
		role: "Operations",
		bio: "System Architecture & Growth",
		color: "bg-red-600"
	},
	{
		name: "Suraj Mandal",
		role: "Intelligence",
		bio: "Neural Networks & Code",
		color: "bg-yellow-500"
	},
];

export default function Elec3DStyle4() {
	const containerRef = useRef(null);
	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["start start", "end end"],
	});

	return (
		<>
			{/* JSON-LD Structured Data */}
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(serviceJsonLd),
				}}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(faqJsonLd),
				}}
			/>
			<div ref={containerRef} className="min-h-screen bg-[#f0f0f0] text-black font-sans selection:bg-black selection:text-white overflow-x-hidden">
			{/* Bauhaus Grid Background */}
			<div className="fixed inset-0 pointer-events-none z-0 opacity-10">
				<div className="absolute top-0 left-1/3 w-px h-full bg-black"></div>
				<div className="absolute top-0 left-2/3 w-px h-full bg-black"></div>
				<div className="absolute top-1/2 left-0 w-full h-px bg-black"></div>
			</div>

			{/* --- HERO SECTION --- */}
			<section className="relative min-h-screen flex flex-col items-center justify-center p-6 overflow-hidden">
				{/* Geometric Composition */}
				<motion.div
					initial={{ scale: 0, rotate: -45 }}
					animate={{ scale: 1, rotate: 0 }}
					transition={{ duration: 1, ease: "circOut" }}
					className="absolute top-20 left-10 w-40 h-40 bg-red-600 rounded-full mix-blend-multiply opacity-90"
				></motion.div>
				<motion.div
					initial={{ scale: 0, x: 100 }}
					animate={{ scale: 1, x: 0 }}
					transition={{ duration: 1, delay: 0.2, ease: "circOut" }}
					className="absolute bottom-20 right-10 w-64 h-64 bg-blue-600 mix-blend-multiply opacity-90 clip-path-polygon-[50%_0%,0%_100%,100%_100%]"
				></motion.div>
				<motion.div
					initial={{ scale: 0, y: 100 }}
					animate={{ scale: 1, y: 0 }}
					transition={{ duration: 1, delay: 0.4, ease: "circOut" }}
					className="absolute top-1/2 right-1/4 w-32 h-32 bg-yellow-500 mix-blend-multiply opacity-90 rotate-12"
				></motion.div>

				<div className="relative z-10 text-center max-w-6xl">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="mb-8 inline-block"
					>
						<div className="bg-black text-white px-6 py-2 font-syne font-bold uppercase tracking-widest text-sm transform -rotate-3 shadow-[4px_4px_0px_0px_rgba(220,38,38,1)]">
							Form Follows Function
						</div>
					</motion.div>

					<h1 className="font-syne text-[15vw] font-bold leading-[0.8] tracking-tighter text-black uppercase mb-8">
						Elec<span className="text-transparent text-stroke-2 text-stroke-black">3D</span>
					</h1>

					<div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
						<motion.div
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.6 }}
							className="flex items-center gap-3"
						>
							<div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 mt-[-2px]">
									<path d="M4 21h16" />
									<path d="M5 21V7h14v14" />
									<path d="M9 16h6" />
									<path d="M12 7v5" />
									<rect x="10" y="12" width="4" height="3" />
								</svg>
							</div>
							<span className="font-syne font-bold text-xl uppercase">Print</span>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8, delay: 0.7 }}
							className="flex items-center gap-3"
						>
							<div className="w-12 h-12 bg-blue-600 flex items-center justify-center text-white clip-path-polygon-[50%_0%,0%_100%,100%_100%]">
								<Plane className="w-6 h-6" />
							</div>
							<span className="font-syne font-bold text-xl uppercase">Play</span>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.8 }}
							className="flex items-center gap-3"
						>
							<div className="w-12 h-12 bg-yellow-500 flex items-center justify-center text-black">
								<Palette className="w-6 h-6" />
							</div>
							<span className="font-syne font-bold text-xl uppercase">Art</span>
						</motion.div>
					</div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 1.0 }}
						className="mt-16 flex justify-center"
					>
						<Link href="https://elec3d.in/" target="_blank">
							<MagneticButton className="h-16 px-10 bg-black text-white font-syne text-lg font-bold uppercase tracking-widest hover:bg-red-600 transition-colors duration-300 flex items-center gap-2 shadow-[4px_4px_0px_0px_rgba(220,38,38,1)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]">
								Launch Website
								<ArrowUpRight className="h-5 w-5" />
							</MagneticButton>
						</Link>
					</motion.div>
				</div>
			</section>

			{/* --- SERVICES (Geometric Cards) --- */}
			<section className="relative py-32 z-10 border-t-4 border-black bg-white">
				<div className="container mx-auto px-6">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{services.map((service, i) => (
							<motion.div
								key={service.id}
								initial={{ opacity: 0, y: 50 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: i * 0.1 }}
								viewport={{ once: true }}
								className="group relative bg-[#f0f0f0] border-2 border-black p-8 hover:-translate-y-2 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all duration-300"
							>
								<div className={`absolute top-0 left-0 w-full h-2 ${service.color}`}></div>

								<div className="mb-8 flex justify-between items-start">
									<div className={`p-4 ${service.color} text-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`}>
										{service.icon}
									</div>
									<span className="font-syne text-4xl font-bold text-black/10">
										{service.id}
									</span>
								</div>

								<h3 className="font-syne text-3xl font-bold uppercase mb-4">
									{service.title}
								</h3>
								<p className="font-inter text-lg text-zinc-600 font-medium leading-relaxed">
									{service.description}
								</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* --- ABOUT (Asymmetrical Grid) --- */}
			<section className="relative py-32 z-10 bg-black text-white overflow-hidden">
				{/* Abstract Shapes */}
				<div className="absolute top-0 right-0 w-1/2 h-full bg-zinc-900 clip-path-polygon-[20%_0%,100%_0,100%_100%,0%_100%] pointer-events-none"></div>

				<div className="container mx-auto px-6 relative z-10">
					<div className="flex flex-col lg:flex-row gap-16 items-start">
						<div className="lg:w-1/3">
							<h2 className="font-syne text-6xl md:text-8xl font-bold uppercase leading-none mb-8">
								The<br />Team
							</h2>
							<div className="w-24 h-4 bg-red-600 mb-8"></div>
							<p className="font-inter text-xl text-zinc-400 font-medium">
								Architects of the physical and digital realms.
							</p>
						</div>

						<div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-6">
							{directors.map((director, i) => (
								<motion.div
									key={i}
									initial={{ opacity: 0, x: 20 }}
									whileInView={{ opacity: 1, x: 0 }}
									transition={{ duration: 0.5, delay: i * 0.1 }}
									viewport={{ once: true }}
									className={`bg-white text-black p-6 border-l-8 ${director.color.replace('bg-', 'border-')} hover:translate-x-2 transition-all duration-300`}
								>
									<h4 className="font-syne text-2xl font-bold uppercase mb-1">
										{director.name}
									</h4>
									<div className="font-mono text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
										{director.role}
									</div>
									<p className="font-inter font-medium">
										{director.bio}
									</p>
								</motion.div>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* --- FAQ (Minimalist) --- */}
			<section className="relative py-32 z-10 bg-[#f0f0f0]">
				<div className="container mx-auto px-6 max-w-4xl">
					<h2 className="font-syne text-5xl font-bold uppercase text-center mb-16">
						<span className="bg-black text-white px-4">Common</span> Questions
					</h2>

					<div className="space-y-4">
						{[
							{ q: "Do you take custom commissions?", a: "Absolutely. We bring your unique ideas to life, from concept art to physical reality." },
							{ q: "Do you build custom drone/robotics projects?", a: "Yes. From heavy-lift quadcopters to complex robotics and automation systems." },
							{ q: "Where are you based?", a: "Proudly engineered and manufactured in India, serving a global clientele." },
						].map((item, i) => (
							<div key={i} className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-1 transition-all duration-300">
								<h3 className="font-syne text-xl font-bold uppercase mb-2 flex items-center gap-2">
									<div className="w-2 h-2 bg-black rounded-full"></div>
									{item.q}
								</h3>
								<p className="font-inter text-zinc-600 pl-4 border-l-2 border-zinc-200">
									{item.a}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* --- FOOTER CTA --- */}
			<section className="relative py-40 flex items-center justify-center overflow-hidden z-10 bg-yellow-400 border-t-4 border-black">
				<div className="text-center">
					<h2 className="font-syne text-[8vw] font-bold text-black uppercase tracking-tighter mb-8 leading-none">
						Start<br />Project
					</h2>
					<a href="https://elec3d.in/" target="_blank">
						<MagneticButton className="h-24 px-16 bg-black text-white font-syne text-2xl font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors duration-300 shadow-[8px_8px_0px_0px_rgba(255,255,255,1)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
							Launch
						</MagneticButton>
					</a>
				</div>
			</section>
		</div>
		</>
	);
}
